/**
 * Real Data Objects API Service
 * 
 * API service for data objects that calls the FastAPI backend with email-based authentication.
 * Provides type adapters to convert backend schema to legacy frontend interface.
 */

import type { DataSource, Folder, FileColumn, FileAnalysis } from '../types';
import type {
    DataObjectSummary,
    DataObjectDetail,
    DataObject,
    DataObjectListResponse as BackendDataObjectListResponse,
    CreateDataObjectRequest,
    UpdateDataObjectRequest,
    DataObjectStatus,
    DataDescription
} from '../types/backend';
import { httpClient, unwrapApiResult } from './authenticatedHttpClient';
import type { DatabricksTableColumn } from '../types/databricks';
import { DEV_MODE, apiPath } from '../config/env';
import { ROOT_FOLDER_ID, FILE_UPLOAD } from '../constants';
import { authManager } from './authManager';
import { acquireTranslationToken } from './tokenUtils';
import type { FrontendRuleType, FrontendRule } from '../types/dqRules';
import { dqRuleService } from './dqRuleService';
import type { BatchCreateRulesRequest, CreateRuleRequest, BatchCreateDatabricksRulesRequest } from '../types/dqRules';
import { FRONTEND_TO_BACKEND_RULE_TYPES, sanitizeDescription } from '../types/dqRules';

// Fixed root folder ID - items assigned to this ID are considered root-level
// const ROOT_FOLDER_UUID = '05c5b1fe-817d-47d6-9fc7-abd6c814a0f0'; // Now imported from constants

// Module-level cache for the real root folder UUID (set during bootstrap)
let _cachedRootFolderUUID: string | null = null;

/**
 * Set the real root folder UUID discovered during bootstrap.
 * adaptBackendDataObject uses this to properly identify root-level data objects.
 * NOTE: The string 'root' is NOT a valid UUID — the backend's __root__ folder
 * sometimes reports id='root' which must never be sent as a folder_id in API calls.
 */
export function setRootFolderUUID(uuid: string | null): void {
    // Reject non-UUID sentinel values like 'root' — they corrupt backend records
    if (uuid && (!uuid.includes('-') || uuid.length < 36)) {
        _cachedRootFolderUUID = null;
        return;
    }
    _cachedRootFolderUUID = uuid;
}

/**
 * Get the cached root folder UUID (may be null if bootstrap hasn't run or root wasn't detected)
 */
export function getRootFolderUUID(): string | null {
    return _cachedRootFolderUUID;
}

/**
 * Check if a folder_id represents the root folder
 */
function isRootFolderId(folderId: string): boolean {
    return folderId === ROOT_FOLDER_ID || folderId === _cachedRootFolderUUID;
}

/**
 * Create DQ rules from stored column configurations after data object creation
 * This handles the deferred rule creation pattern for file upload flows
 */
async function createDeferredRules(dataObjectId: string, columns: FileColumn[], tableName?: string): Promise<void> {
    try {
        // Collect all rules from columns that have them
        const allRules: { columnName: string; rules: FrontendRule[] }[] = [];

        for (const column of columns) {
            if (column.rules) {
                let parsedRules: FrontendRule[] = [];
                try {
                    parsedRules = typeof column.rules === 'string'
                        ? JSON.parse(column.rules)
                        : column.rules;
                } catch (error) {
                    continue;
                }

                if (parsedRules.length > 0) {
                    allRules.push({ columnName: column.name, rules: parsedRules });
                }
            }
        }

        if (allRules.length === 0) {
            return;
        }

        // Transform all rules to batch create format
        const allCreateRules: CreateRuleRequest[] = [];

        for (const { columnName, rules } of allRules) {
            for (const rule of rules) {
                // Validate rule has required fields
                if (!rule.rule_type?.trim()) {
                    continue;
                }

                let parameters = {};
                try {
                    parameters = JSON.parse(rule.rule_logic || '{}');
                } catch {
                    parameters = {};
                }

                const createRule: CreateRuleRequest = {
                    rule_type: FRONTEND_TO_BACKEND_RULE_TYPES[rule.rule_type as FrontendRuleType] || 'null_check',
                    parameters,
                    rule_description: sanitizeDescription(rule.business_meaning || ''),
                    data_object_id: dataObjectId,
                    column_name: columnName
                };

                allCreateRules.push(createRule);
            }
        }

        if (allCreateRules.length === 0) {
            return;
        }

        // Create all rules in batch - use Databricks format if table name provided
        if (tableName) {
            const databricksRules = allCreateRules.map(rule => ({
                ...rule,
                table_name: tableName
            }));
            const batchRequest: BatchCreateDatabricksRulesRequest = { rules: databricksRules };
            await dqRuleService.createRulesBatch(batchRequest);
        } else {
            const batchRequest: BatchCreateRulesRequest = { rules: allCreateRules };
            await dqRuleService.createRulesBatch(batchRequest);
        }
    } catch (error) {
        console.error('[DataObjectService] Failed to create deferred rules:', error);
        // Don't throw - rule creation failure shouldn't break data object creation
    }
}

/**
 * Get current user email for API authentication
 * Uses centralized auth manager instead of localStorage fallback
 */
function getCurrentUserEmail(): string {
    return authManager.getUserEmail();
}

/**
 * Type adapters - convert backend types to legacy frontend interface
 */
function adaptBackendDataObject(
    backendObj: DataObjectSummary | DataObjectDetail | DataObject
): DataSource {
    // Determine file/display type. Prefer explicit backend source_type when available.
    const originalFilename: string = (backendObj as any).original_filename || (backendObj as any).name || '';
    const sourceType: string | undefined = (backendObj as any).source_type || (backendObj as any).extra_metadata?.source_type;
    const isDeltaTable = sourceType === 'delta_table' || originalFilename.toLowerCase().endsWith('.delta');
    const resolvedType = isDeltaTable ? 'databricks' : getFileTypeFromName(originalFilename);
    // Derive a short display name (prefer backend provided name)
    const shortName: string = (backendObj as any).name || originalFilename.replace(/\.(delta|csv|xlsx|xls|json)$/i, '').split('.').slice(-1)[0];

    const mappedStatus = mapBackendStatus(backendObj.status);

    return {
        // Legacy numeric ID - align with bootstrap adapter (last 10 hex chars of UUID sans dashes)
        id: parseInt(backendObj.id.replace(/-/g, '').slice(-10), 16),
        uuid: backendObj.id, // Store original UUID for API calls
        name: shortName,
        originalFileName: originalFilename,
        type: resolvedType,
        status: mappedStatus,
        // Add missing legacy fields with defaults
        size: 'Unknown',
        recordCount: ('data_description' in backendObj && backendObj.data_description?.row_count)
            ? backendObj.data_description.row_count
            : 0,
        uploadedAt: backendObj.created_at,
        // Items assigned to the root folder ID are treated as root-level (no folderId)
        folderId: ('folder_id' in backendObj && backendObj.folder_id && !isRootFolderId(backendObj.folder_id))
            ? parseInt(backendObj.folder_id.replace(/-/g, '').slice(-10), 16)
            : undefined
    };
}

function getFileTypeFromName(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'csv': return 'csv';
        case 'xlsx':
        case 'xls': return 'xlsx';
        case 'json': return 'json';
        case 'delta': return 'databricks';
        default: return 'csv';
    }
}

function mapBackendStatus(status: string): 'processing' | 'processed' | 'error' {
    // Map backend statuses to legacy frontend statuses
    switch (status) {
        case 'uploaded':
        case 'analyzing': return 'processing';
        case 'ready': return 'processed';
        case 'failed': return 'error';
        default:
            return 'processed'; // Default to processed for unknown statuses
    }
}

/**
 * Raw API functions - direct calls to backend endpoints with email-based authentication
 */
const dataObjectApi = {
    async list(params: { folder_id?: string; limit?: number; cursor?: string; user_email: string }) {
        const queryParams = new URLSearchParams();
        queryParams.append('user_email', params.user_email);
        if (params.folder_id) queryParams.append('folder_id', params.folder_id);
        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.cursor) queryParams.append('cursor', params.cursor);

        const endpoint = apiPath(`data-objects?${queryParams.toString()}`);
        return httpClient.get<BackendDataObjectListResponse>(endpoint);
    },

    async get(id: string, options: { include_metadata?: boolean; include_columns?: boolean; include_samples?: boolean; user_email: string; limit?: number; column_limit?: number; target_language?: string }) {
        const queryParams = new URLSearchParams();
        queryParams.append('user_email', options.user_email);
        if (options.include_metadata) queryParams.append('include_metadata', 'true');
        if (options.include_columns) queryParams.append('include_columns', 'true');
        if (options.include_samples) queryParams.append('include_samples', 'true');
        // Try different parameter names that might control column limits
        if (options.limit) queryParams.append('limit', options.limit.toString());
        if (options.column_limit) queryParams.append('column_limit', options.column_limit.toString());
        // Also try some common backend parameter names
        if (options.include_columns) {
            queryParams.append('max_columns', '1000'); // Request lots of columns
            queryParams.append('columns_limit', '1000');
            queryParams.append('all_columns', 'true');
        }

        const needsTranslation = options.target_language && options.target_language !== 'en';
        if (needsTranslation) {
            queryParams.append('target_language', options.target_language!);
        }

        const endpoint = apiPath(`data-objects/${id}?${queryParams.toString()}`);

        // Translation requires a Bearer token for the backend Translation API
        if (needsTranslation) {
            const token = await acquireTranslationToken();
            return httpClient.get<DataObjectDetail>(endpoint, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
        }

        return httpClient.get<DataObjectDetail>(endpoint);
    },

    async create(input: CreateDataObjectRequest & { user_email: string }) {
        return httpClient.post<DataObjectSummary>(apiPath('data-objects/'), input);
    },

    async update(id: string, input: UpdateDataObjectRequest, userEmail: string) {
        const queryParams = new URLSearchParams();
        queryParams.append('user_email', userEmail);

        const endpoint = apiPath(`data-objects/${id}?${queryParams.toString()}`);

        // Translation requires a Bearer token for the backend Translation API
        if (input.target_language && input.target_language !== 'en') {
            const token = await acquireTranslationToken();
            return httpClient.patch<DataObjectSummary>(endpoint, input, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
        }

        return httpClient.patch<DataObjectSummary>(endpoint, input);
    },

    async delete(id: string, userEmail: string) {
        const queryParams = new URLSearchParams();
        queryParams.append('user_email', userEmail);

        const endpoint = apiPath(`data-objects/${id}?${queryParams.toString()}`);
        return httpClient.delete(endpoint);
    }
};

/**
 * High-level service interface - compatible with legacy DataSource interface
 */
// Update request type that includes folder UUID and data object UUID
type DataSourceUpdateRequest = Partial<DataSource> & {
    folderUuid?: string;
    dataObjectUuid?: string;
    description?: string; // For data object description (not in DataSource type)
    columns?: FileColumn[]; // For column metadata updates
    targetLanguage?: string; // For inline translation of metadata
};

export interface DataObjectService {
    getDataSources(folderId?: number): Promise<DataSource[]>;
    getDataSource(id: number | string, includeMetadata?: boolean, targetLanguage?: string): Promise<DataSource>;
    uploadDataSource(file: File, folder?: Folder | number, columns?: any[], description?: string, targetLanguage?: string): Promise<DataSource>;
    replaceDataSourceFile(id: number, file: File, options?: { uuid?: string }): Promise<DataSource>;
    analyzeReplacedFile(uuid: string, file: File, opts?: { description?: string; datasetName?: string; targetLanguage?: string }): Promise<boolean>;
    createDataSourceWithAnalysis(file: File, columns: FileColumn[], folder?: Folder | number, description?: string, targetLanguage?: string): Promise<{ dataSource: DataSource; analysisReady: boolean; jobId?: string; }>;
    updateDataSource(id: number, updates: DataSourceUpdateRequest): Promise<DataSource>;
    deleteDataSource(id: number | string): Promise<void>;
    analyzeFile(file: File): Promise<FileAnalysis>;
    analyzeDataSource(id: number, description?: string): Promise<boolean>;
    analyzeDataSourceWithUuid(uuid: string, description?: string): Promise<boolean>;
    createDeltaTableDataSource(fullName: string, columns: any[], description?: string, datasetNameOverride?: string, folder?: Folder): Promise<DataSource>;
    createDeferredRules(dataObjectId: string, columns: FileColumn[], tableName?: string): Promise<void>;
}

class ApiDataObjectServiceImpl implements DataObjectService {
    async getDataSources(folderId?: number): Promise<DataSource[]> {
        // Convert legacy numeric ID to UUID format if provided
        const folder_id = folderId ? this.numericIdToUuid(folderId) : undefined;
        const user_email = getCurrentUserEmail();

        const result = await dataObjectApi.list({ folder_id, limit: 100, user_email });
        const response = unwrapApiResult(result);

        if (!response || typeof response !== 'object') {
            console.error('[DataObjectService] Invalid response structure:', response);
            return [];
        }

        // Check if response has the expected structure
        if (!('items' in response)) {
            console.error('[DataObjectService] Response missing "items" field. Keys:', Object.keys(response));
            // Maybe the response is directly an array?
            if (Array.isArray(response)) {
                return (response as any[]).map(item => adaptBackendDataObject(item));
            }
            return [];
        }

        // Convert backend data objects to legacy frontend format
        return response.items.map(item => adaptBackendDataObject(item));
    }

    /**
     * Create a data source representing a Databricks Delta table (catalog.schema.table)
     * Minimal metadata; analysis triggered separately.
     */
    async createDeltaTableDataSource(fullName: string, columns: DatabricksTableColumn[], description?: string, datasetNameOverride?: string, folder?: Folder): Promise<DataSource> {
        const user_email = getCurrentUserEmail();

        // Determine folder assignment using the same logic as uploadDataSource
        let folder_id: string | undefined;
        if (folder) {
            folder_id = folder.uuid || this.numericIdToUuid(folder.id);
            if (!folder.uuid) {

            }
        } else {
            // No folder provided - don't include folder_id (let backend default to root)
            folder_id = undefined;

        }

        // For folder_id: use real UUID if available, or omit entirely to let backend default to root.
        // NEVER send 'root' as folder_id - it can corrupt records.
        const resolvedFolderId = folder_id || _cachedRootFolderUUID || undefined;
        const safeFolderId = (resolvedFolderId && resolvedFolderId.includes('-') && resolvedFolderId.length >= 36)
            ? resolvedFolderId
            : undefined; // Omit if not a valid UUID — backend will default to root

        const embeddedPayload: any = {
            user_email,
            name: (datasetNameOverride && datasetNameOverride.trim()) || fullName.split('.').slice(-1)[0],
            original_filename: fullName + '.delta',
            ...(safeFolderId ? { folder_id: safeFolderId } : {}), // Only include if valid UUID
            source_type: 'delta_table',
            data_description: {
                description: description || `Delta table ${fullName}`,
                columns: columns.map(c => ({
                    name: c.name,
                    data_type: c.dataType || 'string',
                    description: c.description || '',
                    sample_values: c.sampleValues || [],
                    analysis_flag: (c as any).analysis_flag !== undefined ? (c as any).analysis_flag : true,
                    has_quality_issues: (c as any).has_quality_issues ?? (c as any).hasQualityIssues ?? false
                }))
            }
        };
        const createEndpoint = apiPath('data-objects/');
        const result = await httpClient.post<DataObjectSummary>(createEndpoint, embeddedPayload);
        const response = unwrapApiResult(result);
        return adaptBackendDataObject(response as any);
    }

    async getDataSource(id: number | string, includeMetadata: boolean = false, targetLanguage?: string): Promise<DataSource> {


        // Try to find the UUID from the cached data first, fall back to numeric conversion
        let uuid: string;

        // If the ID looks like a UUID string (not a number), use it directly
        if (typeof id === 'string' && id.includes('-')) {
            uuid = id;
        } else {
            // For numeric IDs, we need to find the corresponding UUID
            // This is a limitation of the current architecture - we should ideally 
            // pass the UUID directly from the frontend
            uuid = this.numericIdToUuid(id as number);
        }

        const user_email = getCurrentUserEmail();
        const result = await dataObjectApi.get(uuid, {
            include_metadata: includeMetadata,
            include_columns: includeMetadata,
            include_samples: includeMetadata,
            limit: includeMetadata ? 1000 : undefined,
            column_limit: includeMetadata ? 1000 : undefined,
            user_email,
            target_language: (targetLanguage && targetLanguage !== 'en') ? targetLanguage : undefined,
        });
        const response = unwrapApiResult(result);



        if (!response || typeof response !== 'object') {
            throw new Error(`Data object with ID ${id} not found`);
        }

        // Convert backend data object to legacy frontend format
        const dataSource = adaptBackendDataObject(response);

        // If metadata was requested, preserve additional metadata fields for UI consumption
        if (includeMetadata) {
            if ('data_description' in response && response.data_description) {
                (dataSource as any).data_description = response.data_description;
            }
            if ('extra_metadata' in response && response.extra_metadata) {
                (dataSource as any).extra_metadata = response.extra_metadata;
            }
            // Also preserve any other metadata fields that might be useful
            if ('columns' in response && response.columns) {
                (dataSource as any).columns = response.columns;
            }


        }

        return dataSource;
    }

    async uploadDataSource(file: File, folder?: Folder | number, columns?: any[], description?: string, targetLanguage?: string): Promise<DataSource> {
        if (DEV_MODE) {

        }

        // Extract folder UUID - use the stored UUID if folder object, or convert numeric ID if legacy number
        let folder_id: string | undefined;
        if (typeof folder === 'object' && folder !== null) {
            // Folder object - use stored UUID if available, otherwise convert numeric ID
            folder_id = folder.uuid || this.numericIdToUuid(folder.id);
            if (!folder.uuid) {

            }
        } else if (typeof folder === 'number') {
            // Legacy numeric ID - convert to UUID
            folder_id = this.numericIdToUuid(folder);

        } else {
            // No folder provided - don't include folder_id (let backend default to root)
            folder_id = undefined;

        }

        // Build data_description at root level (consolidated contract)
        const data_description: DataDescription | undefined = columns ? {
            columns: columns.map(col => {
                // Handle both FileColumn format and DataObjectData column format
                if ('logical_type' in col) {
                    // DataObjectData format from FileUploadModal
                    return {
                        name: col.name,
                        data_type: col.logical_type,
                        description: col.description || '',
                        sample_values: col.sample_values || col.sampleValues || [],
                        analysis_flag: col.analysis_flag !== undefined ? col.analysis_flag : true,
                        has_quality_issues: col.has_quality_issues ?? col.hasQualityIssues ?? false
                    }
                } else {
                    // FileColumn format (fallback for other uses)
                    const sampleValues = col.sampleValues || [];

                    return {
                        name: col.name,
                        data_type: col.selectedType || col.detectedType,
                        description: col.description || '',
                        sample_values: sampleValues,
                        analysis_flag: col.analysis_flag !== undefined ? col.analysis_flag : true,
                        has_quality_issues: col.has_quality_issues ?? col.hasQualityIssues ?? false
                    }
                }
            }),
            description: description || `Uploaded file: ${file.name}`,
            row_count: 0
        } : {
            description: description || `Uploaded file: ${file.name}`
        };

        const user_email = getCurrentUserEmail();
        const embeddedPayload: any = {
            user_email,
            name: file.name.replace(/\.[^/.]+$/, ''),
            original_filename: file.name,
            folder_id: folder_id,
            source_type: this.getSourceTypeFromFile(file),
            data_description
        };

        // Include target_language for backend reverse-translation when non-English
        if (targetLanguage && targetLanguage !== 'en') {
            embeddedPayload.target_language = targetLanguage;
        }

        if (DEV_MODE) {
            // Deep-copy preview to avoid mutating the actual payload
            const preview = JSON.parse(JSON.stringify(embeddedPayload));
            if (preview.data_description?.columns?.length > 8) {
                preview.data_description = {
                    ...preview.data_description,
                    columns: preview.data_description.columns.slice(0, 8)
                };
            }

        }

        const createEndpoint = apiPath('data-objects/');

        // When translation is requested, add Bearer token
        const headers: Record<string, string> = {};
        if (targetLanguage && targetLanguage !== 'en') {
            const token = await acquireTranslationToken();
            headers['Authorization'] = `Bearer ${token}`;
        }

        const result = await httpClient.post<DataObjectSummary>(createEndpoint, embeddedPayload, { headers });
        const response = unwrapApiResult(result);
        const adaptedDataSource = adaptBackendDataObject(response as any);

        return adaptedDataSource;
    }

    /**
     * Replace the file content of an existing data object without triggering analysis
     * This method updates only the file content, preserving existing metadata
     */
    async replaceDataSourceFile(id: number, file: File, options?: { uuid?: string }): Promise<DataSource> {
        if (DEV_MODE) {

        }

        // Use provided UUID if available, otherwise convert numeric ID to UUID for backend API
        const dataObjectUuid = options?.uuid || this.numericIdToUuid(id);


        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('original_filename', file.name);

        // Log payload details for debugging


        // Use PUT endpoint to replace existing data object file (similar to reportService pattern)
        const updateEndpoint = apiPath(`data-objects/${dataObjectUuid}`);


        let result;
        try {
            // Try PUT method first (for full resource replacement)
            result = await httpClient.put<DataObjectSummary>(updateEndpoint, formData, {
                headers: {
                    // Let browser set Content-Type for FormData (includes boundary)
                }
            });
        } catch (putError) {


            // If PUT fails, try POST to data-objects/ (create new and replace old)
            const createEndpoint = apiPath('data-objects/');
            try {
                result = await httpClient.post<DataObjectSummary>(createEndpoint, formData, {
                    headers: {
                        // Let browser set Content-Type for FormData (includes boundary)
                    }
                });
            } catch (postError) {
                console.error('[DataObjectService] File replacement failed after PUT/POST attempts');
                throw postError;
            }
        }

        const response = unwrapApiResult(result);
        const adaptedDataSource = adaptBackendDataObject(response as any);


        return adaptedDataSource;
    }

    /**
     * After a file has been replaced, perform a GET to retrieve latest description (if not provided)
     * then call profiler analyze endpoint with the new file + description in a single flow.
     * Returns boolean success plus (future) could return report reference.
     */
    async analyzeReplacedFile(uuid: string, file: File, opts?: { description?: string; datasetName?: string; targetLanguage?: string }): Promise<boolean> {
        try {
            // Fetch data object to get folder information
            const user_email = getCurrentUserEmail();
            const apiResult = await dataObjectApi.get(uuid, { include_metadata: true, user_email });
            const current = unwrapApiResult(apiResult) as any;

            const folderId = current?.folder_id;
            const folderInfo = folderId ? {
                folderId: folderId,
                folderUuid: current?.folder_uuid,
                folderName: current?.folder_name
            } : undefined;

            const { reportService } = await import('./reportService');
            // Start async analysis — backend retrieves description from data_object_id
            await reportService.startAsyncAnalysisFile(
                file,
                opts?.datasetName || file.name,
                uuid,
                folderInfo,
                opts?.targetLanguage
            );

            // Update data object status to 'analyzing' to indicate async job started
            try {
                const user_email_update = getCurrentUserEmail();
                await dataObjectApi.update(uuid, {
                    status: 'analyzing'
                }, user_email_update);
            } catch (statusUpdateError) {
                console.error('[DataObjectService] Failed to update status to analyzing after analysis:', {
                    uuid,
                    error: statusUpdateError,
                    errorMessage: statusUpdateError instanceof Error ? statusUpdateError.message : 'Unknown error',
                    stack: statusUpdateError instanceof Error ? statusUpdateError.stack : undefined
                });
                // Don't fail the whole operation if status update fails
            }

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Create data source and wait for analysis completion
     * This method creates a data object, triggers analysis, and waits for completion
     */
    async createDataSourceWithAnalysis(
        file: File,
        columns: FileColumn[],
        folder?: Folder | number,
        description?: string,
        targetLanguage?: string
    ): Promise<{ dataSource: DataSource; analysisReady: boolean; jobId?: string; }> {
        // Create the data object first (pass targetLanguage for reverse-translation)
        const dataSource = await this.uploadDataSource(file, folder, columns, description, targetLanguage);

        // Create any stored DQ rules now that we have a dataObjectId
        if (dataSource.uuid) {
            await createDeferredRules(dataSource.uuid, columns);
        }

        // Set status to 'processing' to indicate analysis is starting
        const dataSourceProcessing = await this.updateDataSource(dataSource.id, {
            status: 'processing',
            dataObjectUuid: dataSource.uuid
        });

        // Import reportService and trigger analysis with the original file
        try {
            const { reportService } = await import('./reportService');

            // Get folder information from the created data source
            const folderInfo = dataSourceProcessing.folderId ? {
                folderId: dataSourceProcessing.folderId,
                folderUuid: (dataSourceProcessing as any).folderUuid,
                folderName: (dataSourceProcessing as any).folderName
            } : undefined;

            // Start async analysis job — backend retrieves description from data_object_id
            const { jobId } = await reportService.startAsyncAnalysisFile(
                file,
                dataSourceProcessing.name,
                dataSourceProcessing.uuid,
                folderInfo,
                targetLanguage
            );

            // Update the data object status to 'analyzing' to indicate async job started
            const updatedDataSource = await this.updateDataSource(dataSourceProcessing.id, {
                status: 'processing', // Keep as processing to indicate work is in progress
                dataObjectUuid: dataSourceProcessing.uuid
            });

            return { dataSource: updatedDataSource, analysisReady: true, jobId };
        } catch (analysisError) {
            console.error('[DataObjectService] Analysis failed for file:', dataSourceProcessing.name, analysisError);

            // Update status to 'error' if analysis fails
            try {
                const errorDataSource = await this.updateDataSource(dataSourceProcessing.id, {
                    status: 'error',
                    dataObjectUuid: dataSourceProcessing.uuid
                });
                return { dataSource: errorDataSource, analysisReady: false };
            } catch (updateError) {
                console.error('[DataObjectService] Failed to update status to error:', updateError);
                return { dataSource: dataSourceProcessing, analysisReady: false };
            }
        }
    }

    async updateDataSource(id: number, updates: DataSourceUpdateRequest): Promise<DataSource> {
        if (DEV_MODE) {

        }

        // Use real data object UUID if provided, otherwise fall back to conversion
        const uuid = updates.dataObjectUuid || this.numericIdToUuid(id);

        const updateInput: UpdateDataObjectRequest = {};
        if (updates.name) updateInput.name = updates.name;
        if (updates.status) updateInput.status = this.mapLegacyStatus(updates.status);

        // Pass target_language for inline translation of metadata
        if (updates.targetLanguage && updates.targetLanguage !== 'en') {
            updateInput.target_language = updates.targetLanguage;
        }

        // Handle description and columns updates via top-level data_description
        if (updates.description !== undefined || updates.columns) {
            updateInput.data_description = {} as any;

            if (updates.description !== undefined) {
                (updateInput.data_description as any).description = updates.description;
            }

            if (updates.columns && updates.columns.length > 0) {
                (updateInput.data_description as any).columns = updates.columns.map(col => ({
                    name: col.name,
                    data_type: col.selectedType || col.detectedType,
                    description: col.description || '',
                    sample_values: col.sampleValues || [],
                    analysis_flag: col.analysis_flag !== undefined ? col.analysis_flag : true,
                    has_quality_issues: col.hasQualityIssues ?? false
                }));
            }
        }

        // Handle folder assignment updates
        if ('folderId' in updates) {
            if (updates.folderId === undefined || updates.folderId === null) {
                // Move to root: we need the REAL root folder UUID (a proper UUID, not the string 'root').
                // CRITICAL: Sending folder_id='root' CORRUPTS records. Sending null gets ignored by PATCH.
                // So we MUST have a real UUID. Try multiple sources to find it.
                let rootUuid = updates.folderUuid || _cachedRootFolderUUID || null;

                // Validate that the candidate is a real UUID
                if (rootUuid && (!rootUuid.includes('-') || rootUuid.length < 36)) {
                    rootUuid = null; // Reject non-UUID values like 'root'
                }

                // If no cached root UUID, try to discover it via the folders API
                if (!rootUuid) {
                    try {
                        const userEmailForRoot = getCurrentUserEmail();
                        const folderQueryParams = new URLSearchParams();
                        folderQueryParams.append('user_email', userEmailForRoot);
                        const foldersResult = await httpClient.get<any>(apiPath(`folders?${folderQueryParams.toString()}`));
                        if (foldersResult.success) {
                            const items = foldersResult.data?.items || foldersResult.data?.folders || foldersResult.data || [];
                            if (Array.isArray(items)) {
                                const rootFromApi = items.find((f: any) => f.name === '__root__');
                                if (rootFromApi?.id && rootFromApi.id.includes('-') && rootFromApi.id.length >= 36) {
                                    rootUuid = rootFromApi.id;
                                    _cachedRootFolderUUID = rootUuid; // Cache for future use
                                }
                            }
                        }
                    } catch {
                        // Folders API fallback failed
                    }
                }

                if (rootUuid) {
                    updateInput.folder_id = rootUuid;
                } else {
                    // Last resort: NEVER send 'root' (corrupts records).
                    // Throw an error so the user gets clear feedback instead of data loss.
                    throw new Error('Cannot move item to main screen: root folder UUID not available. Please refresh the page and try again.');
                }
            } else {
                // Use the provided folder UUID if available, otherwise generate one
                if (updates.folderUuid) {
                    updateInput.folder_id = updates.folderUuid;
                } else {
                    updateInput.folder_id = this.numericIdToUuid(updates.folderId);
                }
            }
        }

        const user_email = getCurrentUserEmail();

        const result = await dataObjectApi.update(uuid, updateInput, user_email);
        const response = unwrapApiResult(result);

        return adaptBackendDataObject(response as any);
    }

    async deleteDataSource(id: number | string): Promise<void> {


        // If it's already a UUID string, use it directly; otherwise convert numeric ID
        const uuid = typeof id === 'string' ? id : this.numericIdToUuid(id);
        const user_email = getCurrentUserEmail();
        const result = await dataObjectApi.delete(uuid, user_email);
        unwrapApiResult(result); // Just unwrap for error handling
    }

    // Helper methods

    /**
     * Create DQ rules from stored column configurations after data object creation
     * This handles the deferred rule creation pattern for both file upload and Databricks flows
     */
    async createDeferredRules(dataObjectId: string, columns: FileColumn[], tableName?: string): Promise<void> {
        return createDeferredRules(dataObjectId, columns, tableName);
    }

    private getSourceTypeFromFile(file: File): string {
        const ext = file.name.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'csv': return 'csv';
            case 'xlsx':
            case 'xls': return 'excel';
            case 'json': return 'json';
            case 'parquet': return 'parquet';
            default: return 'csv';
        }
    }

    private numericIdToUuid(_id: number): string {
        throw new Error(`[DataObjectService] Real UUID required — numeric ID conversion is not supported. Ensure the caller provides a UUID string. Received numeric ID: ${_id}`);
    }

    async analyzeDataSource(id: number, description?: string): Promise<boolean> {
        try {
            // Convert numeric ID to UUID for API call
            const uuid = this.numericIdToUuid(id);

            // First fetch the current data object to get its description if not provided
            if (!description) {
                const currentDataObject = await this.getDataSource(id, true);
                description = (currentDataObject as any).description || '';
            }

            // Call the analyzeDataSourceWithUuid method to avoid code duplication
            return await this.analyzeDataSourceWithUuid(uuid, description);
        } catch (error) {
            console.error('[DataObjectService] Failed to analyze data source:', error);
            return false;
        }
    }

    async analyzeDataSourceWithUuid(uuid: string, description?: string): Promise<boolean> {
        try {
            // If no description provided, fetch the current data object
            if (!description) {
                const user_email = getCurrentUserEmail();
                const apiResult = await dataObjectApi.get(uuid, { include_metadata: true, user_email });
                const currentDataObject = unwrapApiResult(apiResult);

                // Extract description from various possible locations
                const extraMetadata = (currentDataObject as any).extra_metadata;
                const dataDesc = (currentDataObject as any).data_description;
                description = dataDesc?.description ||
                    extraMetadata?.data_description?.description ||
                    (currentDataObject as any).description || '';
            }

            // For re-analysis, we need to trigger analysis on the existing data object
            // Option 1: Use a dedicated re-analyze endpoint that takes UUID + description
            // Option 2: Update the data object's description and trigger analysis

            try {
                // Try a dedicated re-analyze endpoint first
                const reAnalyzePayload = {
                    description: description || '',
                    data_object_uuid: uuid
                };

                const response = await httpClient.post<any>(
                    apiPath(`profiler/re-analyze/${uuid}`),
                    reAnalyzePayload,
                    {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );

                const result = unwrapApiResult(response);

                // Update data object status to 'ready' after successful analysis
                try {
                    const user_email = getCurrentUserEmail();
                    const updateResult = await dataObjectApi.update(uuid, {
                        status: 'ready'
                    }, user_email);
                } catch (statusUpdateError) {
                    console.error('[DataObjectService] Failed to update status to ready after re-analysis:', {
                        uuid,
                        error: statusUpdateError,
                        errorMessage: statusUpdateError instanceof Error ? statusUpdateError.message : 'Unknown error'
                    });
                    // Don't fail the whole operation if status update fails
                }

                return true;
            } catch (error) {
                console.error('[DataObjectService] Re-analysis endpoint failed, trying fallback:', error);

                // Fallback: Update the data object with new description and analysis flag
                const updatePayload = {
                    description: description,
                    trigger_analysis: true
                };

                const user_email = getCurrentUserEmail();
                await dataObjectApi.update(uuid, updatePayload as any, user_email);

                // Update data object status to 'ready' after successful fallback analysis
                try {
                    const updateResult = await dataObjectApi.update(uuid, {
                        status: 'ready'
                    }, user_email);
                } catch (statusUpdateError) {
                    console.error('[DataObjectService] Failed to update status to ready after fallback analysis:', {
                        uuid,
                        error: statusUpdateError,
                        errorMessage: statusUpdateError instanceof Error ? statusUpdateError.message : 'Unknown error'
                    });
                    // Don't fail the whole operation if status update fails
                }

                return true;
            }
        } catch (error) {
            console.error('[DataObjectService] Failed to analyze data source with UUID:', error);
            return false;
        }
    }

    async analyzeFile(file: File): Promise<FileAnalysis> {
        // Use real file parsing to extract actual sample values instead of mock data

        try {
            // Parse the file to extract real data
            const text = await file.text();
            let rows: Record<string, any>[] = [];

            const fileExtension = file.name.split('.').pop()?.toLowerCase();

            if (fileExtension === 'csv') {
                // Parse CSV
                const lines = text.split('\n').filter(line => line.trim());
                if (lines.length === 0) throw new Error('Empty file');

                const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
                rows = lines.slice(1).map(line => {
                    const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
                    const row: Record<string, any> = {};
                    headers.forEach((header, idx) => {
                        row[header] = values[idx] || '';
                    });
                    return row;
                });
            } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                // For Excel files, we need to use a proper parser
                // For now, return a basic structure - this would need SheetJS or similar
                throw new Error('Excel parsing not yet implemented in API service');
            }

            // Extract columns and sample values from real data
            const columnsSet = new Set<string>();
            rows.slice(0, 50).forEach(r => Object.keys(r).forEach(k => columnsSet.add(k)));
            const columnNames = Array.from(columnsSet);

            const columns = columnNames.map(name => {
                const sampleValues = rows.slice(0, FILE_UPLOAD.PREVIEW_ROWS)
                    .map(r => r[name])
                    .filter(v => v !== undefined && v !== null && v !== '')
                    .map(v => String(v));

                // Simple type inference based on sample values
                const inferType = (values: string[]): string => {
                    if (values.length === 0) return 'String';
                    const boolMatch = values.every(v => /^(true|false)$/i.test(v.trim()));
                    if (boolMatch) return 'Boolean';
                    const intMatch = values.every(v => /^-?\d+$/.test(v.trim()));
                    if (intMatch) return 'Integer';
                    const floatMatch = values.every(v => /^-?\d+(\.\d+)?$/.test(v.trim()));
                    if (floatMatch) return 'Float';
                    // Timestamp: date + time component
                    const tsMatch = values.every(v => !isNaN(Date.parse(v.trim())) && /[T :]/.test(v.trim()));
                    if (tsMatch) return 'Timestamp';
                    const dateMatch = values.every(v => !isNaN(Date.parse(v)));
                    if (dateMatch) return 'Date';
                    return 'String';
                };

                return {
                    name,
                    detectedType: inferType(sampleValues) as any,
                    sampleValues: sampleValues.slice(0, 8) // Take up to 8 sample values
                };
            });

            const previewData = rows.slice(0, FILE_UPLOAD.PREVIEW_ROWS);

            return {
                fileName: file.name,
                fileSize: file.size,
                columns,
                recordCount: Math.min(5, rows.length),
                totalRows: rows.length,
                previewData,
                source: 'api'
            };

        } catch (error) {
            console.error('[DataObjectService] Failed to parse file:', error);
            // If parsing fails, return minimal structure
            return {
                fileName: file.name,
                fileSize: file.size,
                columns: [],
                recordCount: 0,
                totalRows: 0,
                previewData: [],
                source: 'api'
            };
        }
    }

    private mapLegacyStatus(status: 'processing' | 'processed' | 'error' | 'analyzing'): DataObjectStatus {
        // Map legacy frontend statuses to backend statuses
        switch (status) {
            case 'processing': return 'analyzing';
            case 'analyzing': return 'analyzing';
            case 'processed': return 'ready';
            case 'error': return 'failed';
            default: return 'ready';
        }
    }
}

// Export singleton instance
export const apiDataObjectService: DataObjectService = new ApiDataObjectServiceImpl();